import React from "react";
import { motion } from "framer-motion";

/**
 * motionSafe — drop-in replacement for framer-motion's `motion` proxy that
 * adds React's suppressHydrationWarning to every underlying DOM element.
 *
 * Why: prerendered snapshots serialize the FINAL animation state
 * (opacity: 1, transform: none). At hydration, framer-motion re-applies each
 * component's `initial` style (e.g. translateY(14px)) to the props of the
 * host element, which would otherwise trigger React hydration-mismatch
 * errors. With suppression, React keeps the prerendered (visible) DOM and
 * framer's animation loop simply confirms the final state — no flash, no
 * error, no content wipe. Entrance animations still run on subsequent
 * client-side route changes.
 */
const cache = new Map();

export const motionSafe = new Proxy(motion, {
  get(target, key) {
    if (typeof key !== "string") return target[key];
    if (!cache.has(key)) {
      const Base = target[key];
      if (typeof Base !== "function" && typeof Base !== "object") return Base;
      const Wrapped = React.forwardRef((props, ref) =>
        React.createElement(Base, { suppressHydrationWarning: true, ...props, ref }),
      );
      Wrapped.displayName = `motionSafe(${key})`;
      cache.set(key, Wrapped);
    }
    return cache.get(key);
  },
});
